import { useState, useEffect } from 'react';
import { ArticleList } from './components/ArticleList';
import { ArticleReader } from './components/ArticleReader';
import { AuthForm } from './components/AuthForm';
import { InstallPrompt } from './components/InstallPrompt';
import { useAuth } from './hooks/useAuth';
import { useArticles, Article } from './hooks/useArticles';
import './styles/mobile.css';

type View = 'list' | 'reader';

export default function App() {
  const { user, loading: authLoading, signUp, signIn, signOut } = useAuth();
  const { articles, loading: articlesLoading, refreshArticles, getArticle, updateReadingProgress } = useArticles(user?.id);

  const [currentView, setCurrentView] = useState<View>('list');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      if (currentView === 'reader') {
        setCurrentView('list');
        setSelectedArticle(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentView]);

  const handleSelectArticle = async (article: Article) => {
    // Fetch full article data if needed
    const fullArticle = await getArticle(article.id);
    if (fullArticle) {
      setSelectedArticle(fullArticle);
      setCurrentView('reader');
      history.pushState({ view: 'reader', articleId: article.id }, '', `#article/${article.id}`);
    }
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedArticle(null);
    history.pushState({ view: 'list' }, '', '#');
  };

  const handleProgressUpdate = (progress: number) => {
    if (selectedArticle) {
      updateReadingProgress(selectedArticle.id, progress);
    }
  };

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration.scope);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }, []);

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="app loading-screen">
        <div className="loading-spinner-large" />
        <p>Loading...</p>
        <style>{`
          .loading-screen {
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 1rem;
            background: var(--bg-primary, #0a0a0a);
            color: var(--text-secondary, #888888);
          }
          .loading-spinner-large {
            width: 40px;
            height: 40px;
            border: 3px solid rgba(249, 115, 22, 0.2);
            border-top-color: var(--accent, #f97316);
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Show auth form if not logged in
  if (!user) {
    return <AuthForm onSignUp={signUp} onSignIn={signIn} />;
  }

  // Main app UI when authenticated
  return (
    <div className="app">
      {currentView === 'list' && (
        <>
          <header className="app-header">
            <h1>Read Later</h1>
            <div className="header-right">
              <span className="article-count">
                {articlesLoading ? '...' : `${articles.length} articles`}
              </span>
              <button
                className="sign-out-btn"
                onClick={signOut}
                title={`Signed in as ${user.email}`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </button>
            </div>
          </header>
          <main className="app-main">
            {articlesLoading ? (
              <div className="loading-articles">
                <div className="loading-spinner-large" />
                <p>Loading articles...</p>
              </div>
            ) : (
              <ArticleList
                articles={articles}
                onSelectArticle={handleSelectArticle}
                onRefresh={refreshArticles}
              />
            )}
          </main>
        </>
      )}

      {currentView === 'reader' && selectedArticle && (
        <ArticleReader
          article={selectedArticle}
          onBack={handleBackToList}
          onProgressUpdate={handleProgressUpdate}
        />
      )}

      <InstallPrompt />

      <style>{`
        .header-right {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .sign-out-btn {
          background: none;
          border: none;
          color: var(--text-secondary, #888888);
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s, background 0.2s;
        }
        .sign-out-btn:hover {
          color: var(--text-primary, #ffffff);
          background: var(--bg-secondary, #141414);
        }
        .loading-articles {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          gap: 1rem;
          color: var(--text-secondary, #888888);
        }
        .loading-spinner-large {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(249, 115, 22, 0.2);
          border-top-color: var(--accent, #f97316);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
